
import { Modal } from 'bootstrap';
import { defineComponent, ref, inject, onMounted, watch } from 'vue';
import { Apollo, Notify } from '@/core/services';
import { GET_PRODUCT_GROUP } from '@/modules/catalog/product-groups/graphql/Queries';
import { CREATE_PRODUCT_GROUP } from '@/modules/catalog/product-groups/graphql/Mutations';
import { AddProductGroupForm } from '@/modules/catalog/product-groups/interfaces/index';
import { useI18n } from 'vue-i18n';
import InnerLoader from '@/components/InnerLoader.vue';

export default defineComponent({
    name: 'add new customer group',
    components: {
        InnerLoader
    },

    setup() {
        const modal: any = ref();
        const loading = ref(false);
        const emitter: any = inject('emitter');
        const i18n = useI18n();
        const addProductGroupForm = ref<null | HTMLFormElement>(null);
        const discountTypeError = ref('');

        // Given Add Customer Group Form Interface
        const productGroupData = ref<AddProductGroupForm>({
            name: '',
            uuid: '',
            allow_discount: false,
            discount_type: '',
            discount_value: ''
        });

        // Clear discount type error when user selects a value
        const clearDiscountTypeError = () => {
            discountTypeError.value = '';
        };

        // Form validation rules
        const formRules = ref({
            name: [
                {
                    required: true,
                    message: `${i18n.t('message.NAME')} ${i18n.t('message.IS_REQUIRED')}`,
                    trigger: 'blur'
                }
            ],
            discount_type: [
                {
                    validator: (rule: any, value: string, callback: any) => {
                        if (productGroupData.value.allow_discount && !value) {
                            callback(new Error(`${i18n.t('message.DISCOUNT_TYPE')} ${i18n.t('message.IS_REQUIRED')}`));
                        } else {
                            callback();
                        }
                    },
                    trigger: 'change'
                }
            ],
            discount_value: [
                {
                    validator: (rule: any, value: string, callback: any) => {
                        if (productGroupData.value.allow_discount && !value) {
                            callback(new Error(`${i18n.t('message.DISCOUNT_VALUE')} ${i18n.t('message.IS_REQUIRED')}`));
                        } else {
                            callback();
                        }
                    },
                    trigger: 'blur'
                }
            ]
        });

        // Helper function to safely parse discount value
        const parseDiscountValue = (value: string): number | null => {
            if (!value || value.trim() === '') return null;
            const parsed = parseFloat(value);
            return isNaN(parsed) ? null : parsed;
        };

        // Submit Hanlder Request
        const submitHandlerForm = async () => {
            addProductGroupForm.value?.validate(async (valid: boolean) => {
                // Additional validation for discount fields when allow_discount is true
                if (productGroupData.value.allow_discount) {
                    if (!productGroupData.value.discount_type) {
                        discountTypeError.value = `${i18n.t('message.DISCOUNT_TYPE')} ${i18n.t('message.IS_REQUIRED')}`;
                        return;
                    }
                    // if (!productGroupData.value.discount_value) {
                    //     Notify.error(`${i18n.t('message.DISCOUNT_VALUE')} ${i18n.t('message.IS_REQUIRED')}`);
                    //     return;
                    // }
                }

                if (valid) {
                    const formData = {
                        name: productGroupData.value.name,
                        discount_type: productGroupData.value.allow_discount ? productGroupData.value.discount_type : null,
                        discount_value: productGroupData.value.allow_discount ? parseDiscountValue(productGroupData.value.discount_value) : null
                    };

                    loading.value = true;
                    await Apollo.mutate({
                        mutation: CREATE_PRODUCT_GROUP,
                        variables: { input: formData },
                        update: (store, { data: { create_product_group } }) => {
                            const pervious_record = store.readQuery({
                                query: GET_PRODUCT_GROUP,
                                variables: {
                                    page: 1,
                                    limit: 10
                                }
                            }) as Record<any, any>;
                            store.writeQuery({
                                query: GET_PRODUCT_GROUP,
                                variables: {
                                    page: 1,
                                    limit: 10
                                },
                                data: {
                                    product_groups: {
                                        ...pervious_record.product_groups,
                                        data: [create_product_group, ...pervious_record.product_groups.data]
                                    }
                                }
                            });
                            modal.value.hide();
                            loading.value = false;
                            Notify.success(`${i18n.t('message.RECORD_ADDED_SUCCESSFULLY')}`);
                        }
                    }).catch(() => {
                        loading.value = false;
                    });
                }
            });
        };

        // Reset Form Data On Model Popup
        const resetForm = () => {
            // Reset form data
            productGroupData.value = {
                name: '',
                uuid: '',
                allow_discount: false,
                discount_type: '',
                discount_value: ''
            };

            // Clear error messages
            discountTypeError.value = '';

            // Reset form fields if form is available
            if (addProductGroupForm.value) {
                addProductGroupForm.value.resetFields();
            }
        };

        // Run resetForm when component mounts
        onMounted(() => {
            resetForm();
        });

        // Emitter To Open Model
        emitter.on('AddProductGroupAction', () => {
            resetForm();
            modal.value = new Modal(document.getElementById('modal_add_product_group'));
            modal.value.show();
        });

        return {
            loading,
            productGroupData,
            addProductGroupForm,
            submitHandlerForm,
            discountTypeError,
            clearDiscountTypeError,
            formRules
        };
    }
});
